import React from 'react';
import { Box, Paper, Skeleton, ToggleButton, Typography, styled } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';

import { Row } from 'ui/Flex';
import { useScenarioChartData } from '../hooks/useScenarioChartData';
import { StyledToggleButtonGroup } from './StyledToggleButtonGroup';

const ChartSkeleton = styled(Skeleton)(({ theme }) => ({
    width: '100%',
    height: '100%',
    borderRadius: theme.shape.borderRadius
}));

interface ScenarioChartCardProps {
    title: string;
    subtitle: string;
    data: { label: string; value: number }[];
    color: string;
    maxValue?: number;
    stepSize?: number;
    loading?: boolean;
    platform?: string;
    onPlatformChange?: (platform?: string) => void;
}

export const ScenarioChartCard: React.FC<ScenarioChartCardProps> = ({
    title,
    subtitle,
    data,
    color,
    maxValue,
    stepSize,
    loading,
    platform,
    onPlatformChange
}) => {
    const handlePlatformChange = React.useCallback(
        (_: React.MouseEvent, value: string | null) => {
            onPlatformChange?.(value);
        },
        [onPlatformChange]
    );
    const { chartData, chartOptions } = useScenarioChartData({ data, color, maxValue, stepSize, loading });
    const hasData = React.useMemo(() => data.length > 0 && data.some(d => d.value > 0), [data]);

    const renderChartContent = React.useCallback(() => {
        if (loading) {
            return <ChartSkeleton variant="rectangular" />;
        }
        if (!hasData) {
            return (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No data available
                </Typography>
            );
        }
        return <Bar data={chartData} options={chartOptions} />;
    }, [loading, hasData, chartData, chartOptions]);

    return (
        <Paper variant="outlined" sx={{ py: 3, px: 2, height: '100%' }} elevation={0}>
            <Row align="space-between" valign="flex-start">
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {subtitle}
                    </Typography>
                </Box>
                <StyledToggleButtonGroup
                    value={platform}
                    exclusive
                    onChange={handlePlatformChange}
                    size="small"
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="mobile">Mobile</ToggleButton>
                    <ToggleButton value="web">Web</ToggleButton>
                </StyledToggleButtonGroup>
            </Row>
            <Box mt={2} height={350} display="flex" alignItems="center" justifyContent="center">
                {renderChartContent()}
            </Box>
        </Paper>
    );
};
